import { useState, useEffect, useContext } from 'react';
import get from 'lodash.get';
import Logger from 'utilities/logger';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import withPermissionProtection from 'hoc/withPermissionProtection';
import SnackbarContext from 'context/Snackbar/Context';

import MaskedInput from 'react-text-mask';
import InputAdornment from '@material-ui/core/InputAdornment';
import Grid from '@material-ui/core/Grid';
import ErrorIcon from '@material-ui/icons/Error';
import CheckIcon from '@material-ui/icons/Check';

import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import Dropdown from 'components/CncUI/Dropdown';
import Button from 'components/CncUI/Button';
import Modal from 'components/CncUI/Modal';
import Loader from 'components/Loader';

import useGetPayrollUser from '../hooks/useGetPayrollUser';
import useUpdateTaxDocument from '../hooks/useUpdateTaxDocument';

import { Employers } from 'constants/taxDocuments';
import { ssnMask, yearMask } from 'constants/masks';
import isSSN from 'utilities/validateSSN';

const styles = ({ palette }) => ({
  description: {
    paddingBottom: 10,
  },
  placeHolder: {
    height: '270px',
    padding: 15,
    display: 'grid',
    placeItems: 'center',
    boxSizing: 'border-box',
  },
  success: {
    color: palette.cncUI.alert.green.primary,
  },
  error: {
    color: palette.cncUI.alert.red.primary,
  },
});

const Editor = props => {
  const { open, data, classes, onClose, onSuccess } = props;
  const logger = Logger('W2Document.Editor');
  const { id, year, employer = '', name } = data || {};
  const notify = useContext(SnackbarContext);
  const [form, setForm] = useState({ id, name, employer, year, ssn: '' });
  const [state, setState] = useState({ loading: false, user: null, error: '' });
  const getUser = useGetPayrollUser();
  const updateDocument = useUpdateTaxDocument();
  const modify = data => setState(old => ({ ...old, ...data }));
  const update = data => setForm(old => ({ ...old, ...data }));

  useEffect(() => {
    if (form.ssn && isSSN(form.ssn)) {
      modify({ loading: true });
      getUser(form.ssn)
        .then(user => modify({ user, error: user ? '' : 'Not Found' }))
        .catch(err => modify({ error: err.message }))
        .finally(() => modify({ loading: false }));
    }
  }, [form.ssn]);

  const submit = () => {
    notify({
      show: true,
      type: 'info',
      title: null,
      message: `Processing the document now.
        Please remain on the page until it is complete.`,
    });
    modify({ loading: true });
    updateDocument({ ...form, year: parseInt(form.year, 10) })
      .then(() => {
        notify({
          show: true,
          type: 'success',
          title: 'Success',
          message: 'The document has been uploaded successfully.',
        });
        onSuccess();
        onClose();
      })
      .catch(err => {
        const { message: errMessage } = err || {};
        const formattedErrorMessage = () => {
          if (errMessage.startsWith('GraphQL error:')) {
            return errMessage.substring(14);
          }
          return `We ran into a problem updating that document.
            Please refresh the page and try again or contact support.`;
        };
        notify({ show: true, type: 'error', message: formattedErrorMessage() });
        modify({ loading: false });
        logger.error(err);
      });
  };

  const valid = () => {
    const { name, year, employer, ssn } = form;
    if (state.loading) return false;
    if (state.error) return false;
    if (!name) return false;
    if (!employer) return false;
    if (!isSSN(ssn)) return false;
    if (String(year).length !== 4 || typeof parseInt(year, 10) !== 'number')
      return false;
    return true;
  };

  const displayPayrollProfile = () => {
    if (!isSSN(form.ssn)) return false;
    if (state.user === null) return false;
    if (state.error) return false;
    if (state.loading) return false;
    return true;
  };

  const noPayrollUserFound = () => {
    if (state.loading) return false;
    return !!state.error;
  };

  const endAdornment = () => {
    if (state.loading) {
      return (
        <InputAdornment position="end">
          <Loader size={15} />
        </InputAdornment>
      );
    }
    if (state.error) {
      return (
        <InputAdornment position="end">
          <ErrorIcon className={classes.error} />
        </InputAdornment>
      );
    }
    if (state.user) {
      return (
        <InputAdornment position="end">
          <CheckIcon className={classes.success} />
        </InputAdornment>
      );
    }
    return null;
  };

  return (
    <Modal open={open} onClose={onClose} id="EditorModal">
      <Modal.Title id="EditorTitle">Edit Tax Document</Modal.Title>
      <Modal.Content>
        <Modal.ContentText
          id="EditorDescription"
          className={classes.description}
        >
          To update the selected tax document update the incorrect information
          below and press save. To move this document to a new user enter the
          correct ssn and press save.
        </Modal.ContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              value={form.name}
              label="Document Type"
              onChange={e => update({ name: e.target.value })}
              InputProps={{
                inputProps: {
                  name: 'documentType',
                  id: 'documentType',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MaskedInput
              mask={yearMask}
              keepCharPositions={true}
              value={form.year}
              onChange={e => update({ year: e.target.value })}
              render={(ref, props) => (
                <TextField
                  inputRef={ref}
                  {...props}
                  fullWidth
                  label="Tax Document Year"
                  InputProps={{
                    inputProps: {
                      id: 'documentYear',
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Dropdown
              fullWidth
              label="Employer Name"
              value={form.employer || ''}
              onChange={e => update({ employer: e.target.value })}
              id="documentEmployer"
            >
              {Employers.map(employer => (
                <Dropdown.Item key={employer} value={employer}>
                  {employer}
                </Dropdown.Item>
              ))}
            </Dropdown>
          </Grid>
          <Grid item xs={12}>
            <MaskedInput
              mask={ssnMask}
              value={form.ssn}
              onChange={e => update({ ssn: e.target.value })}
              render={(ref, props) => (
                <TextField
                  inputRef={ref}
                  {...props}
                  fullWidth
                  label="Social Security Number"
                  InputProps={{
                    inputProps: {
                      id: 'documentSsn',
                    },
                    endAdornment: endAdornment(),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        {displayPayrollProfile() && (
          <Grid container spacing={2} id="ProfileContainer">
            <Grid item xs={12}>
              <Typography variant="h4" id="ProfileHeader">
                Payroll Profile:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">First Name:</Typography>
              <Typography variant="body1" id="ProfileFirstName">
                {get(state, 'user.firstName', '')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">Middle Initial:</Typography>
              <Typography variant="body1" id="ProfileMiddleInitial">
                {get(state, 'user.middleInitial', '') || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">Last Name:</Typography>
              <Typography variant="body1" id="ProfileLastName">
                {get(state, 'user.lastName', '')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">Address:</Typography>
              <Typography variant="body1" id="ProfileAddressLine">
                {get(state, 'user.addressLine1', '')}
              </Typography>
              <Typography variant="body1" id="ProfileAddressCityStateZip">
                {get(state, 'user.city', '')}, {get(state, 'user.state', '')}{' '}
                {get(state, 'user.zipCode', '')}
              </Typography>
            </Grid>
          </Grid>
        )}
        {!displayPayrollProfile() && !noPayrollUserFound() && (
          <div className={classes.placeHolder}>
            <Typography id="ProfilePlaceHolder">
              Please enter a SSN to retrieve the payroll profile information.
            </Typography>
          </div>
        )}
        {!displayPayrollProfile() && noPayrollUserFound() && (
          <div className={classes.placeHolder}>
            <Typography id="ProfileError">
              There is no user in payroll under that SSN. Please enter a new
              SSN.
            </Typography>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button secondary id="CancelButton" onClick={onClose}>
          Cancel
        </Button>
        <Button id="SaveButton" onClick={submit} disabled={!valid()}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default compose(
  withPermissionProtection('users:read'),
  withStyles(styles),
)(Editor);
